import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Mail,
  Plus,
  Info,
  CheckCircle2,
  Circle,
  Pause,
  Lock,
  UserCog,
  PauseCircle,
} from 'lucide-react';
import { PageHeader } from '../../components/common/PageHeader';
import { useEmailSequences, SequenceSummary } from '../../hooks/useEmailSequences';
import { CreateSequenceModal } from '../../components/email/CreateSequenceModal';
import { LeadAssignedEmailSettings } from '../../components/leads/LeadAssignedEmailSettings';
import { useAuth } from '../../hooks/useAuth';

type OutletCtx = {
  userData?: { companyId?: string; role?: string };
};

// Roles that can author/edit/pause email sequences. Brokers/credit_analysts/
// broker_assistants get a read-only view of the same surface so they
// understand what their leads are receiving — write actions are hidden,
// not the data.
const MANAGER_ROLES = new Set<string>([
  'super_admin',
  'administrator',
  'office_admin',
  'marketing',
]);

// Email Automation: stat tiles, sequences table, explainer.
//
// Sent counts come from the delivery log. Open and click rates are genuinely
// not measured (nothing records opens for these sends) and render as a dash;
// they are not a number waiting to be wired up. Do not put a plausible
// placeholder in either tile.

function fmtPct(ratio: number | null): string {
  if (ratio === null) return '—';
  return `${Math.round(ratio * 100)}%`;
}

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const lower = status.toLowerCase();
  if (lower === 'active') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
        <Circle className="w-2 h-2 fill-emerald-400" />
        Active
      </span>
    );
  }
  if (lower === 'paused') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/15 text-amber-300 border border-amber-500/30">
        <Pause className="w-2 h-2" />
        Paused
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-500/15 text-gray-300 border border-gray-500/30">
      Draft
    </span>
  );
};

const HOW_IT_WORKS: Array<{ title: string; body: string }> = [
  {
    title: 'VALUTTEN decides the timing',
    body: 'Each step is scheduled from the day the lead was enrolled, so a three-day gap stays a three-day gap. Steps go out at your configured send hour in your timezone.',
  },
  {
    title: 'Resend is the postman',
    body: 'Resend delivers the email itself. Every send is recorded in your audit trail whether it succeeds or fails.',
  },
  {
    title: 'Your branding on every email',
    body: 'The logo, brand colour, ABN, address and phone you saved in the Setup Wizard are rendered into every sequence email, along with your sender identification and unsubscribe link.',
  },
  {
    title: 'Emails sent from your domain',
    body: 'Sent from noreply@yourdomain, not from VALUTTEN, with replies going to the assigned broker. Each company gets its own verified sending domain.',
  },
  {
    title: 'Consent and unsubscribe are enforced',
    body: 'A lead who has not agreed to marketing email is never enrolled and never sent to, consent is re-checked before every send, and an unsubscribe stops the sequence immediately. Spam Act 2003 sender identification is on every message.',
  },
  {
    title: 'Nothing sends without real data',
    body: 'If a template needs something we cannot read from the lead record, such as a lender or an interest rate, that step is blocked rather than filled in with a placeholder. Those templates are preview-only in the gallery.',
  },
];

const LeadCaptureEmailPage: React.FC = () => {
  const { userData } = useOutletContext<OutletCtx>();
  const companyId = userData?.companyId;
  const { claims, isFounder } = useAuth();
  const role = claims?.role ?? userData?.role ?? '';
  const canManage = isFounder || MANAGER_ROLES.has(role);

  const {
    sequences,
    stats,
    loading,
    error,
    refresh,
    updateSequenceStatus,
  } = useEmailSequences(companyId);

  const [createOpen, setCreateOpen] = useState(false);
  const hasSequences = sequences.length > 0;

  const handleToggle = async (seq: SequenceSummary) => {
    if (!canManage) return;
    const next =
      seq.status === 'active'
        ? 'paused'
        : seq.status === 'paused'
        ? 'active'
        : 'active';
    try {
      await updateSequenceStatus(seq.sequenceId, next);
    } catch (err) {
      console.error('[LeadCaptureEmailPage] updateSequenceStatus failed', err);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Mail}
        title="Email Automation"
        subtitle="Multi-step nurture sequences for leads you enrol, sent from your own domain"
        onRefresh={refresh}
        isRefreshing={loading}
        actions={
          canManage ? (
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs inline-flex items-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" />
              New sequence
            </button>
          ) : undefined
        }
      />

      {!canManage && (
        <div className="rounded-md border border-blue-500/30 bg-blue-500/10 p-3 text-xs text-blue-200 flex items-start gap-2">
          <Lock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-semibold text-blue-100">View-only access</div>
            <div className="text-blue-300/90 mt-0.5">
              Email automation is managed by your team's admin or marketing
              role. You can see what's running so you understand what your
              leads are receiving, but creating, editing, and pausing
              sequences happens at the admin level. You can still enrol your
              own leads in any active sequence from the lead inbox.
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="rounded-md border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">
          {error}
        </div>
      )}

      {/* Sending gate banner.
          The page used to imply mail was going out: a stat tile reading "via
          Resend API" and an empty state promising "no manual sending
          required", while every enrolment was in fact being held because
          sending is not switched on. Say so, at the top. */}
      {stats && !stats.sendingEnabled && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200 flex items-start gap-2">
          <PauseCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-semibold text-amber-100">
              Sending is not switched on for your company yet
            </div>
            <div className="text-amber-300/90 mt-0.5">
              Sequences run and leads can be enrolled, but no email is sent.
              {stats.heldContactCount > 0 && (
                <>
                  {' '}
                  {stats.heldContactCount.toLocaleString()}{' '}
                  {stats.heldContactCount === 1 ? 'enrolment is' : 'enrolments are'}{' '}
                  on hold at their current step and will resume from that same
                  step once sending is enabled. Nothing is skipped.
                </>
              )}{' '}
              Contact VALUTTEN when you are ready to go live.
            </div>
          </div>
        </div>
      )}

      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="rounded-lg border border-gray-700 bg-gray-800/40 border-t-2 border-t-blue-500 p-4">
          <div className="text-[11px] uppercase tracking-wider text-gray-400">
            Emails sent (30d)
          </div>
          <div className="mt-1 text-3xl font-bold text-blue-400">
            {(stats?.emailsSent30d ?? 0).toLocaleString()}
          </div>
          <div className="mt-1 text-[11px] text-gray-500">
            from your sending domain
          </div>
        </div>
        {/* Open and click rates are not measured: nothing records opens for
            these sends. A dash is the honest answer; the tiles used to sit
            beside an industry benchmark as though a real figure were coming. */}
        <div className="rounded-lg border border-gray-700 bg-gray-800/40 border-t-2 border-t-emerald-500 p-4">
          <div className="text-[11px] uppercase tracking-wider text-gray-400">
            Avg open rate
          </div>
          <div className="mt-1 text-3xl font-bold text-emerald-400">
            {fmtPct(stats?.avgOpenRate ?? null)}
          </div>
          <div className="mt-1 text-[11px] text-gray-500">not tracked yet</div>
        </div>
        <div className="rounded-lg border border-gray-700 bg-gray-800/40 border-t-2 border-t-amber-500 p-4">
          <div className="text-[11px] uppercase tracking-wider text-gray-400">
            Avg click rate
          </div>
          <div className="mt-1 text-3xl font-bold text-amber-400">
            {fmtPct(stats?.avgClickRate ?? null)}
          </div>
          <div className="mt-1 text-[11px] text-gray-500">not tracked yet</div>
        </div>
        <div className="rounded-lg border border-gray-700 bg-gray-800/40 border-t-2 border-t-purple-500 p-4">
          <div className="text-[11px] uppercase tracking-wider text-gray-400">
            Active sequences
          </div>
          <div className="mt-1 text-3xl font-bold text-purple-400">
            {stats?.activeSequenceCount ?? 0}
          </div>
          <div className="mt-1 text-[11px] text-gray-500">
            {(stats?.enrolledContactCount ?? 0).toLocaleString()} contacts
            enrolled
            {(stats?.heldContactCount ?? 0) > 0 && (
              <span className="text-amber-400">
                {' '}· {(stats?.heldContactCount ?? 0).toLocaleString()} on hold
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Sequences table */}
      <div className="rounded-lg border border-gray-700 bg-gray-800/40 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-700">
          <h3 className="text-sm font-semibold text-white">
            Active sequences
          </h3>
        </div>
        {!hasSequences ? (
          <div className="p-8 text-center">
            <Mail className="w-8 h-8 text-gray-500 mx-auto mb-3" />
            <h3 className="text-sm font-semibold text-white mb-2">
              No email sequences yet
            </h3>
            <p className="text-xs text-gray-400 max-w-md mx-auto">
              Create a sequence to follow up with new leads over several days.
              You enrol a lead from the lead inbox and VALUTTEN sends each step
              on schedule. Automatic enrolment from lead or deal events is not
              built yet, so enrolling is a manual step for now.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-900/50 text-gray-400 uppercase">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Sequence</th>
                  <th className="px-4 py-3 text-left font-medium">Trigger</th>
                  <th className="px-4 py-3 text-right font-medium">Steps</th>
                  <th className="px-4 py-3 text-right font-medium">
                    Enrolled
                  </th>
                  <th className="px-4 py-3 text-left font-medium">Status</th>
                  <th className="px-4 py-3 text-left font-medium">
                    Managed by
                  </th>
                  {canManage && (
                    <th className="px-4 py-3 text-right font-medium"></th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {sequences.map((seq) => (
                  <tr key={seq.sequenceId} className="text-gray-200">
                    <td className="px-4 py-3">
                      <div className="font-medium text-white">{seq.name}</div>
                      {seq.description && (
                        <div className="text-[11px] text-gray-500 mt-0.5 line-clamp-1">
                          {seq.description}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-400 font-mono text-[11px]">
                      {seq.trigger}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {seq.stepCount}
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      {seq.enrolledCount.toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={seq.status} />
                    </td>
                    <td className="px-4 py-3 text-gray-400">
                      {seq.lastEditedByEmail ? (
                        <span className="inline-flex items-center gap-1.5">
                          <UserCog className="w-3 h-3 text-gray-500" />
                          <span className="truncate max-w-[180px]">
                            {seq.lastEditedByEmail}
                          </span>
                          {seq.lastEditedByRole && (
                            <span className="text-[10px] text-gray-500">
                              · {seq.lastEditedByRole}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-gray-600">—</span>
                      )}
                    </td>
                    {canManage && (
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => handleToggle(seq)}
                          className="px-2 py-1 rounded-md bg-gray-700 hover:bg-gray-600 text-white text-xs"
                        >
                          {seq.status === 'active' ? 'Pause' : 'Activate'}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* How it works card */}
      <div className="rounded-lg border-l-4 border-l-blue-500 border-t border-r border-b border-gray-700 bg-gray-800/40 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Info className="w-4 h-4 text-blue-400" />
          <h3 className="text-sm font-semibold text-white">
            How email automation works
          </h3>
        </div>
        <ul className="space-y-3">
          {HOW_IT_WORKS.map((item) => (
            <li key={item.title} className="flex gap-3">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div className="text-xs">
                <div className="font-semibold text-white">{item.title}</div>
                <div className="text-gray-400 mt-0.5 leading-relaxed">
                  {item.body}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <LeadAssignedEmailSettings companyId={companyId} canManage={canManage} />

      {canManage && (
        <CreateSequenceModal
          companyId={companyId}
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={() => refresh()}
        />
      )}
    </div>
  );
};

export default LeadCaptureEmailPage;
